import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import Toggle from 'Components/common/Toggle';
import { confirmationPopupState } from 'Components/common/confirmationPopup';
import { toasterState } from 'Components/common/toaster';
import fieldMappingState from 'Stores/fieldMapping';
import { userDetail } from 'Stores/userDetail';
import integrationDataMap from 'Pages/integration/integrationDataMap';
import {
  EMPTY_FUNCTION,
  MX_ENRICHMENT_EVENTS,
  TOAST_MSG_TYPES,
} from 'Utils/constants';
import { Utils } from 'Utils/utils';

const emptyCbk = () => {};

function SettingsSwitch(props) {
  const {
    title,
    description,
    isChecked = false,
    onToggle = () => {},
    className = '',
    animateSwitch,
    setSwitchAnimation,
  } = props;

  const ref = useRef();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (animateSwitch && ref.current)
      if (checked) {
        setTimeout(() => {
          setSwitchAnimation(false);
          setChecked(false);
        }, 2000);
      } else {
        ref.current.classList.add('click');
        setTimeout(() => {
          setChecked(true);
          ref.current.classList.remove('click');
        }, 2000);
      }
  }, [animateSwitch, checked]);

  return (
    <>
      <div
        className={`setting-row-wrap${className ? ` ${className}` : ''}`}
        ref={ref}
      >
        <div className="setting-message-wrap">
          <div className="title">{title}</div>
          <div className="message">{description}</div>
        </div>
        <Toggle
          firstText="Off"
          toggle={isChecked || checked}
          cbk={onToggle}
          lastText="On"
        />
      </div>
    </>
  );
}

function CardWithTitle(props) {
  const { title, children, cardClassName = '' } = props;
  return (
    <>
      <div className="preferences-title">{title}</div>
      <div className={`preferences-card ${cardClassName}`}>{children}</div>
    </>
  );
}

/**
 * This component shares styles with crmSettings
 */

const EnrichmentSettings = observer(props => {
  const {
    callback = EMPTY_FUNCTION,
    showToast = true,
    enableEdit = false,
    animateSwitch = false,
    isEnrichmentSettingsFetched = false,
  } = props;
  const { crmType } = useParams();
  const currentCRMData = integrationDataMap[crmType?.toUpperCase()];
  const [switchAnimation, setSwitchAnimation] = useState(false);

  const hasLeads =
    currentCRMData?.sublinks?.find(data => data.linkKey === 'field-mapping')
      ?.subMenu?.length === 2 || false;

  useEffect(() => {
    if (isEnrichmentSettingsFetched) setSwitchAnimation(animateSwitch);
  }, [isEnrichmentSettingsFetched]);

  const hasEditAccess =
    enableEdit || fieldMappingState.enrichmentSettingsData?.isAdmin || false;
  const crmName =
    currentCRMData?.title
      ?.split(' ')
      ?.map(data => `${data.substring(0, 1).toUpperCase()}${data.substring(1)}`)
      ?.join(' ') ?? crmType;
  const { userInfo } = userDetail;
  const { enrichmentCreditsRemaining } = userInfo;
  const isNotificationEnabled =
    fieldMappingState.enrichmentSettingsData?.autoEnrichConfig
      ?.crmNotificationEnable;

  const toggleAutoEnrichment = async checked => {
    if (enrichmentCreditsRemaining === 0) {
      confirmationPopupState.setPopupValues({
        title: ' No Enrichment credits left!',
        content: `You have utilized all of your enrichment credits. Hence Auto-enrichment to your ${crmName} CRM is disabled. Please contact our <a href="mailto:support@adapt.io">support</a> to add enrichment credits to your account.`,
        actionBtnText: 'OK',
        needCancelBtn: false,
        callback: emptyCbk,
        customClassName: 'no-enrich-credits',
      });
      confirmationPopupState.setShowPopup(true);
    } else {
      Utils.mixpanelTrack(MX_ENRICHMENT_EVENTS.AUTO_ENRICHMENT_TOGGLED, {
        value: checked,
      });
      const newData = {
        autoEnrichConfig: {
          crmContact: checked,
          ...(!checked &&
            isNotificationEnabled && { crmNotificationEnable: false }),
        },
      };
      const success = await fieldMappingState.updateEnrichmentSettings(
        crmType.toUpperCase(),
        newData,
      );
      if (success) {
        toasterState.setToastMsg(
          'Enrichment settings updated successfully',
          TOAST_MSG_TYPES.SUCCESS_NEW,
        );
        fieldMappingState.enrichmentSettingsData = {
          ...fieldMappingState.enrichmentSettingsData,
          autoEnrichConfig: {
            ...fieldMappingState.enrichmentSettingsData.autoEnrichConfig,
            ...(newData?.autoEnrichConfig || {}),
          },
        };
      }
      callback(success);
    }
  };

  const toggleEmailForAutoEnrichment = async checked => {
    Utils.mixpanelTrack(
      MX_ENRICHMENT_EVENTS.AUTO_ENRICHMENT_NOTIFICATION_TOGGLED,
      {
        value: checked,
      },
    );
    const isSuccess = await fieldMappingState.updateEnrichmentSettings(
      crmType.toUpperCase(),
      {
        autoEnrichConfig: { crmNotificationEnable: checked },
      },
    );
    if (isSuccess)
      toasterState.setToastMsg(
        'Enrichment settings updated successfully',
        TOAST_MSG_TYPES.SUCCESS_NEW,
      );
  };

  return (
    <div
      className={`enrichment-settings crm-settings-wrapper${
        hasEditAccess ? '' : ' no-edit'
      }`}
    >
      {switchAnimation && <div className="mask" />}
      <CardWithTitle
        title="Auto Enrichment"
        cardClassName={switchAnimation ? ' animate-switch' : ''}
      >
        {hasLeads && (
          <>
            <SettingsSwitch
              crmType={crmType}
              isChecked={
                fieldMappingState.enrichmentSettingsData?.autoEnrichConfig
                  ?.crmLead
              }
              onToggle={checked =>
                fieldMappingState.updateEnrichmentSettings(
                  crmType.toUpperCase(),
                  { autoEnrichConfig: { crmLead: checked } },
                )
              }
              title="Leads"
              description={`Automatically enrich new Leads added to ${crmName} with Adapt's
          data every 30 minutes.`}
            />
            <div className="divider" />
          </>
        )}
        <SettingsSwitch
          crmType={crmType}
          animateSwitch={switchAnimation}
          setSwitchAnimation={setSwitchAnimation}
          isChecked={
            fieldMappingState.enrichmentSettingsData?.autoEnrichConfig
              ?.crmContact
          }
          onToggle={toggleAutoEnrichment}
          title="Contacts"
          className="enrichment-enable"
          description={`Automatically enrich new Contacts added to ${crmName} with Adapt's
          data every 30 minutes.`}
        />
      </CardWithTitle>
      <CardWithTitle title="Notification">
        <SettingsSwitch
          className={`enrichment-email-toggle${
            !(
              fieldMappingState.enrichmentSettingsData?.autoEnrichConfig
                ?.crmContact || false
            )
              ? ' disabled'
              : ''
          }`}
          isChecked={isNotificationEnabled}
          crmType={crmType}
          onToggle={toggleEmailForAutoEnrichment}
          title="Email notification for Auto enrichment"
          description="I&#39;d like to receive a weekly email notification on Auto Enrichment every Monday."
        />
      </CardWithTitle>
    </div>
  );
});

export default EnrichmentSettings;
